import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import setObj from "lodash/set";
import { setProp } from "Store/Actions";
import { setPropT } from "Store/Actions/ts";
import { ContractHubItem } from "../Services/ContractHubService";
import {
  fetchContractHubList,
  fetchContractHubStats,
  fetchContractHubSummary,
} from "../Services/ContractHubThunk";

// ─── State Shape ──────────────────────────────────────────────────────────────

interface ContractHubPagination {
  currentPage: number;
  itemsPerPage: number;
  totalCount: number;
  autoRefresh: boolean;
}

interface ContractHubState {
  /** Grid list state */
  list: {
    loading: boolean;
    data: ContractHubItem[];
    error: string | null;
  };
  /** Summary chip counts */
  stats: {
    loading: boolean;
    data: [string, number][] | null;
    error: string | null;
  };
  /** Banner summary counters keyed by status (e.g. { Total: 42, Effective: 15 }) */
  summary: {
    loading: boolean;
    data: Record<string, number>;
    error: string | null;
  };
  /** Current search term (bound to GridActionBar) */
  searchValue: string;
  /** Account type filter (survives navigate away / remount) */
  accountType: string;
  /** Pagination */
  pagination: ContractHubPagination;
  /** Snackbar / notification message */
  snackbarMessage: string;
}

// ─── Initial State ────────────────────────────────────────────────────────────

const initialState: ContractHubState = {
  list: {
    loading: false,
    data: [],
    error: null,
  },
  stats: {
    loading: false,
    data: null,
    error: null,
  },
  summary: {
    loading: false,
    data: {},
    error: null,
  },
  searchValue: "",
  accountType: "all",
  pagination: {
    currentPage: 1,
    itemsPerPage: 10,
    totalCount: 0,
    autoRefresh: false,
  },
  snackbarMessage: "",
};

// ─── Slice ────────────────────────────────────────────────────────────────────

const contractHubSlice = createSlice({
  name: "contractHub",
  initialState,
  reducers: {
    /** Update the search input only — page reset happens on Enter / clear */
    setSearchValue(state, action: PayloadAction<string>) {
      state.searchValue = action.payload;
    },
    /** Persist account type filter across remounts */
    setAccountType(state, action: PayloadAction<string>) {
      state.accountType = action.payload;
    },
    /** Update a single numeric pagination key */
    updatePagination(
      state,
      action: PayloadAction<{
        key: Exclude<keyof ContractHubPagination, 'autoRefresh'>;
        value: number;
      }>,
    ) {
      state.pagination[action.payload.key] = action.payload.value;
    },
    /** Reset pagination to page 1 */
    resetPagination(state) {
      state.pagination.currentPage = 1;
    },
    /** Toggle auto-refresh on/off */
    setAutoRefresh(state, action: PayloadAction<boolean>) {
      state.pagination.autoRefresh = action.payload;
    },
    /** Clear the snackbar message */
    clearSnackbarMessage(state) {
      state.snackbarMessage = "";
    },
  },
  extraReducers(builder) {
    // ── fetchContractHubList ──────────────────────────────────────────────────
    builder.addCase(fetchContractHubList.pending, (state) => {
      state.list.loading = true;
      state.list.error = null;
    });
    builder.addCase(fetchContractHubList.fulfilled, (state, action) => {
      state.list.loading = false;
      state.list.data = action.payload.data;
      state.pagination.totalCount = action.payload.total_count;
    });
    builder.addCase(fetchContractHubList.rejected, (state, action) => {
      state.list.loading = false;
      state.list.error = (action.payload as string) || "Failed to load contracts";
      state.snackbarMessage = state.list.error;
    });

    // ── fetchContractHubStats ─────────────────────────────────────────────────
    builder.addCase(fetchContractHubStats.pending, (state) => {
      state.stats.loading = true;
      state.stats.error = null;
    });
    builder.addCase(fetchContractHubStats.fulfilled, (state, action) => {
      state.stats.loading = false;
      state.stats.data = action.payload.data;
    });
    builder.addCase(fetchContractHubStats.rejected, (state, action) => {
      state.stats.loading = false;
      state.stats.error =
        (action.payload as string) || "Failed to load stats";
    });

    // ── fetchContractHubSummary ───────────────────────────────────────────────
    builder.addCase(fetchContractHubSummary.pending, (state) => {
      state.summary.loading = true;
      state.summary.error = null;
    });
    builder.addCase(fetchContractHubSummary.fulfilled, (state, action) => {
      state.summary.loading = false;
      state.summary.data =
        action.payload?.data && typeof action.payload.data === "object"
          ? action.payload.data
          : {};
    });
    builder.addCase(fetchContractHubSummary.rejected, (state, action) => {
      state.summary.loading = false;
      state.summary.error =
        (action.payload as string) || "Failed to load summary";
    });

    // ── setProp (shared action used across all slices) ────────────────────────
    builder.addCase(setProp, (state, action: setPropT) => {
      if (action.payload.slice === contractHubSlice.name) {
        setObj(state, action.payload.key, action.payload.value);
      }
    });
  },
});

export const {
  setSearchValue,
  setAccountType,
  updatePagination,
  resetPagination,
  setAutoRefresh,
  clearSnackbarMessage,
} = contractHubSlice.actions;

export default contractHubSlice.reducer;
